import {
  Image,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { Download } from "lucide-react";
import React from "react";
import DocMobilLogo from "../../../assets/svgs/DocMobilLogoWithText.svg";
import { Button } from "../../ui/button";
import GenericPDFDocument from "../GenericPDFDocument";

function getAgeFromDOB(dob) {
  if (!dob) return "N/A";
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

const styles = StyleSheet.create({
  section: {
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: "1px solid #ccc",
    gap: 20,
  },
  label: {
    fontSize: 12,
    color: "#888",
    fontWeight: 500,
    marginBottom: 2,
  },
  value: {
    fontSize: 14,
    color: "#222",
    fontWeight: 600,
    marginBottom: 8,
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1E1E1E",
  },
  subheading: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1E1E1E",
  },
  watermarkContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 0.08,
    zIndex: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  watermarkImage: {
    width: 350,
    height: 350,
    objectFit: "contain",
  },
});

const DownloadMedicalCertificateForm = ({ displayData, appointmentId }) => {
  const generatePDFContent = () => [
    // Watermark
    <View key="watermark" style={styles.watermarkContainer}>
      <Image src={DocMobilLogo} style={styles.watermarkImage} />
    </View>,
    // Certificate Content
    <View key="content" style={{ position: "relative", zIndex: 1 }}>
      <Text style={styles.subheading}>DocMobil</Text>
      <View
        style={{ height: 2, backgroundColor: "#3B82F6", marginBottom: 16 }}
      />
      <View style={{ marginBottom: 16 }}>
        <Text style={styles.value}>
          <Text style={styles.label}>Date: </Text>
          {displayData?.createdAt
            ? new Date(displayData.createdAt).toLocaleDateString()
            : new Date().toLocaleDateString()}
        </Text>
        <Text style={styles.value}>
          <Text style={styles.label}>Patient Name: </Text>
          {(displayData?.appointment?.patient?.firstName || "N/A") +
            " " +
            (displayData?.appointment?.patient?.lastName || "N/A")}
        </Text>
        <Text style={styles.value}>
          <Text style={styles.label}>Age: </Text>
          {getAgeFromDOB(displayData?.appointment?.patient?.dob)}
        </Text>
        <Text style={styles.value}>
          <Text style={styles.label}>Patient ID: </Text>
          {displayData?.appointment?.patient?.id || "N/A"}
        </Text>
      </View>
      <Text style={[styles.value, { marginBottom: 16, fontWeight: "500" }]}>
        This is to certify that the patient was examined and treated at our
        clinic/hospital on{" "}
        <Text style={{ fontWeight: 700 }}>
          {displayData?.createdAt
            ? new Date(displayData.createdAt).toLocaleDateString()
            : new Date().toLocaleDateString()}
        </Text>
        . The patient has been diagnosed with the following medical condition:
      </Text>
      <View style={{ marginBottom: 16 }}>
        <Text style={styles.subheading}>Medical Condition/Diagnosis:</Text>
        <Text style={[styles.value, { fontWeight: "500" }]}>
          {displayData?.diagnosis || "N/A"}
        </Text>
      </View>
      <View style={{ marginBottom: 16 }}>
        <Text style={styles.subheading}>Recommendations:</Text>
        <Text style={[styles.value, { fontWeight: "500" }]}>
          {displayData?.recommendations || "N/A"}
        </Text>
      </View>
      <View style={{ marginTop: 48 }}>
        <Text style={styles.subheading}>Authorized Medical Officer</Text>
        <Text style={styles.value}>
          {(displayData?.appointment?.doctor?.firstName || "N/A") +
            " " +
            (displayData?.appointment?.doctor?.lastName || "N/A")}
        </Text>
        <Text style={styles.value}>
          {displayData?.appointment?.doctor?.qualifications?.join(", ")}
        </Text>

        <Text
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#1E1E1E",
            marginBottom: "8px",
            textAlign: "left",
          }}
        >
          Signature
        </Text>
        {displayData?.appointment?.doctor?.signature?.url ? (
          <View style={{ width: 150, marginLeft: -20 }}>
            <Image
              src={`${displayData.appointment.doctor.signature.url}?ext=.png`}
              style={{ width: 150, objectFit: "contain" }}
            />
          </View>
        ) : (
          <Text style={{ fontSize: 12, color: "#888" }}>
            No signature available
          </Text>
        )}
        <Text style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
          Certificate ID: {displayData?.id || "N/A"}
        </Text>
      </View>
    </View>,
  ];

  return (
    <PDFDownloadLink
      document={
        <GenericPDFDocument heading="Medical Certificate">
          {generatePDFContent()}
        </GenericPDFDocument>
      }
      fileName={`medical_certificate_${appointmentId}.pdf`}
    >
      {({ loading: pdfLoading }) => (
        <Button variant="primary" disabled={pdfLoading}>
          <Download className="mr-2 h-4 w-4" />
          {pdfLoading ? "Preparing PDF..." : "Download"}
        </Button>
      )}
    </PDFDownloadLink>
  );
};

export default DownloadMedicalCertificateForm;
